import React, { useContext, useEffect, useState } from 'react'

import { Button, Header, Grid, Confirm, Divider } from 'semantic-ui-react'

import Data from '../../api/Data'
import { ITeam } from '../../api/Data'
import { teamAvatarSize, teamLogoSize, teamWatermarkSize } from '../../config/constants'
import { createWhiteWatermarkBlob } from '../../utils/watermark'
import { GlobalContext } from '../../contexts/GlobalContext'
import { UserContext } from '../../contexts/UserContext'

import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'
import ImageUploader from '../../components/modals/ImageUploader'

import BrandingLogos from './BrandingLogos'
import BrandingStyle from './BrandingStyle'

var tinycolor = require('tinycolor2')

const MyBrandingPage = () => {
  const { hasUserMadeChange, dispatchGlobalContext } = useContext(GlobalContext)
  const { dispatchUserContext } = useContext(UserContext)

  // States
  const [team, setTeam] = useState<ITeam | null>(null)

  const [loading, setIsLoading] = useState<boolean>(false)

  const [showIconUploadModal, setShowIconUploadModal] = useState(false)
  const [showFullLogoUploadModal, setShowFullLogoUploadModal] = useState(false)
  const [showWatermarkUploadModal, setShowWatermarkUploadModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    const fetchTeam = async () => {
      const { data } = await Data.getMyTeam()

      setTeam(data)
    }
    fetchTeam()
  }, [])

  const handleCancelAvatarUpload = () => {
    setShowIconUploadModal(false)
  }

  const handleCancelFullLogoUpload = () => {
    setShowFullLogoUploadModal(false)
  }

  const handleCancelWatermarkUpload = () => {
    setShowWatermarkUploadModal(false)
  }

  // Change team avatar
  const handleSaveTeamAvatar = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamAvatar(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred when during image upload')
    }

    const { logoAvatarUrl } = data

    setTeam(data)

    dispatchUserContext({ type: 'update user profile', payload: { logoAvatarUrl } })

    setIsLoading(false)

    return setShowIconUploadModal(false)
  }

  // Change full team logo
  const handleSaveFullTeamLogo = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamLogo(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred during image upload')
    }

    const { logoUrl } = data

    setTeam(data)
    dispatchUserContext({ type: 'update user profile', payload: { logoUrl } })

    setIsLoading(false)

    return setShowFullLogoUploadModal(false)
  }

  // Change team watermark
  const handleSaveTeamWatermark = async (imageBlob: Blob) => {
    const formData = new FormData()

    formData.append('imageBlob', imageBlob)

    setIsLoading(true)

    const { error, data } = await Data.updateTeamWatermark(formData)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred when during image upload')
    }

    const { logoWatermarkUrl } = data

    setTeam(data)

    dispatchUserContext({ type: 'update user profile', payload: { logoWatermarkUrl } })

    setIsLoading(false)

    return setShowWatermarkUploadModal(false)
  }

  const handleChangeFont = (newFont: { family: string }) => {
    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    const newFontFamily = newFont.family

    setTeam((preState: any) => ({ ...preState, primaryFont: newFontFamily }))
  }

  const handleChangePrimaryColor = (newColor: { hex: string }) => {
    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    const hexColor = newColor.hex

    setTeam((preState: any) => ({
      ...preState,
      ...{
        primaryColor: hexColor,
        buttonTextColor: tinycolor(hexColor).getBrightness() >= 200 ? '#000000' : '#FFFFFF',
      },
    }))
  }

  const handleChangeSecondaryColor = (newColor: { hex: string }) => {
    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    const hexColor = newColor.hex

    setTeam((preState: any) => ({ ...preState, secondaryColor: hexColor }))
  }

  const handleSubmitStyle = async () => {
    setIsLoading(true)

    const { data, error } = await Data.updateTeam(team)

    if (error) {
      setIsLoading(false)
      return alert('An error occurred during the update')
    }

    const { primaryFont, primaryColor, secondaryColor, buttonTextColor } = data

    setTeam(data)

    dispatchUserContext({
      type: 'update user profile',
      payload: { primaryFont, primaryColor, secondaryColor, buttonTextColor },
    })

    dispatchGlobalContext({ type: 'set user has made changes', payload: false })

    setIsLoading(false)

    return setShowConfirmModal(true)
  }

  if (!team) return null

  const { logoAvatarUrl, logoUrl, logoWatermarkUrl, primaryFont, primaryColor, secondaryColor } = team

  return (
    <Section>
      <>
        {/* title */}
        <Title title="">
          <>
            <Button compact primary loading={loading} disabled={!hasUserMadeChange} onClick={handleSubmitStyle}>
              Save
            </Button>
          </>
        </Title>

        <SubSection>
          <>
            <Header as="h1" style={{ fontWeight: 'bold' }}>
              Branding
              <Header.Subheader>
                Tailor your brand—logos, colors, fonts, and visual styling for landing pages and video templates.
              </Header.Subheader>
            </Header>

            <Divider style={{ margin: '2rem 0' }} />

            <Grid columns={1} divided="vertically">
              {/* Logos */}
              <BrandingLogos
                logoAvatarUrl={logoAvatarUrl}
                logoUrl={logoUrl}
                logoWatermarkUrl={logoWatermarkUrl}
                setShowIconUploadModal={setShowIconUploadModal}
                setShowFullLogoUploadModal={setShowFullLogoUploadModal}
                setShowWatermarkUploadModal={setShowWatermarkUploadModal}
              />

              {/* Style */}
              <BrandingStyle
                primaryFont={primaryFont}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                handleChangeFont={handleChangeFont}
                handleChangePrimaryColor={handleChangePrimaryColor}
                handleChangeSecondaryColor={handleChangeSecondaryColor}
              />
            </Grid>
          </>
        </SubSection>

        {/* Team avatar */}
        {showIconUploadModal && (
          <ImageUploader
            inputImageType={teamAvatarSize.inputImageType}
            outputImageType={teamAvatarSize.outputImageType}
            imageIsAvatar={teamAvatarSize.imageIsAvatar}
            imageSize={{ width: teamAvatarSize.width, height: teamAvatarSize.height }}
            loading={loading}
            handleSave={handleSaveTeamAvatar}
            handleCancel={handleCancelAvatarUpload}
          />
        )}

        {/* Full team logo */}
        {showFullLogoUploadModal && (
          <ImageUploader
            inputImageType={teamLogoSize.inputImageType}
            outputImageType={teamLogoSize.outputImageType}
            imageIsAvatar={teamLogoSize.imageIsAvatar}
            imageSize={{ width: teamLogoSize.width, height: teamLogoSize.height }}
            loading={loading}
            handleSave={handleSaveFullTeamLogo}
            handleCancel={handleCancelFullLogoUpload}
          />
        )}

        {/* Team watermark */}
        {showWatermarkUploadModal && (
          <ImageUploader
            inputImageType={teamWatermarkSize.inputImageType}
            outputImageType={teamWatermarkSize.outputImageType}
            imageIsAvatar={teamWatermarkSize.imageIsAvatar}
            imageSize={{ width: teamWatermarkSize.width, height: teamWatermarkSize.height }}
            loading={loading}
            transformImage={createWhiteWatermarkBlob}
            handleSave={handleSaveTeamWatermark}
            handleCancel={handleCancelWatermarkUpload}
          />
        )}

        {/* confirmation modal */}
        {showConfirmModal && (
          <Confirm
            open={showConfirmModal}
            header="Branding Updated"
            content="Note: Your changes will only apply to new projects, existing projects will not be a affected by the changes."
            confirmButton="OK"
            cancelButton={null}
            onConfirm={() => setShowConfirmModal(false)}
          />
        )}
      </>
    </Section>
  )
}

export default MyBrandingPage
